// Gemeinsame Prüf- und Komprimierlogik für Bild-Uploads (Profilbild, Logo).
// Genutzt von ImagePicker und dem Profilbild-Picker der Profilseite, damit
// Typprüfung, Größenlimit und automatische Komprimierung (Issue #363) nur an
// einer Stelle liegen.
import { compressImageToLimit } from "@/utils/imageCompression";

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];

// Sicherheitsobergrenze für das Rohbild, bevor es im Browser überhaupt
// dekodiert wird (verhindert Memory-Spikes bei Riesendateien).
const HARD_MAX_BYTES = 30 * 1024 * 1024;

export type ImageUploadResult =
  | { ok: true; file: File; compressionInfo: string }
  | { ok: false; reason: "type" | "tooLarge" };

/**
 * Prüft Dateityp und Größe und komprimiert das Bild bei Bedarf so weit, dass
 * es unter `maxFileSizeMB` passt. Liefert die (ggf. komprimierte) Datei plus
 * einen Hinweistext, wenn komprimiert wurde.
 */
export async function prepareImageUpload(file: File, maxFileSizeMB: number): Promise<ImageUploadResult> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return { ok: false, reason: "type" };
  }
  if (file.size > HARD_MAX_BYTES) {
    return { ok: false, reason: "tooLarge" };
  }

  const maxBytes = maxFileSizeMB * 1024 * 1024;
  let finalFile = file;
  let wasCompressed = false;
  try {
    const result = await compressImageToLimit(file, maxBytes);
    finalFile = result.file;
    wasCompressed = result.compressed;
  } catch {
    finalFile = file;
  }

  if (finalFile.size > maxBytes) {
    // Selbst nach maximaler Komprimierung noch zu groß.
    return { ok: false, reason: "tooLarge" };
  }

  const mb = (finalFile.size / (1024 * 1024)).toFixed(1);
  const compressionInfo = wasCompressed
    ? `Das Bild war zu groß und wurde automatisch komprimiert (jetzt ca. ${mb} MB).`
    : "";
  return { ok: true, file: finalFile, compressionInfo };
}

/** Fehlermeldung für den Anwender zu einem fehlgeschlagenen Upload. */
export function imageUploadErrorText(reason: "type" | "tooLarge", maxFileSizeMB: number): string {
  if (reason === "type") return "Nur JPG- und PNG-Dateien sind erlaubt.";
  return `Die ausgewählte Datei konnte nicht ausreichend verkleinert werden (max. ${maxFileSizeMB} MB). Bitte wählen Sie ein kleineres Bild.`;
}
